import dayjs from 'dayjs';
import { DATEFORMAT_LOCAL_STATE } from '@ps20-frontend/utils';
import { Produkt } from '../model';

const LONG_VORVERTRAG_LAUFZEIT_YEARS = 2;
const VORVERTRAG_ABLAUF_WINDOW_MONTHS = 12;
const SHORT_VORVERTRAG_1Y_WINDOW_DAYS = 365;
const SHORT_VORVERTRAG_3Y_WINDOW_DAYS = 730;
const HF_PARSE_FORMAT = 'DD.MM.';
const MINDESTLAUFZEIT_YEARS = 1;

export const getCurrentDay = (): dayjs.Dayjs => {
  return dayjs();
};

function hfPlusYears(
  beginnLocal: string,
  hfMMDD: string,
  years: number,
): string | undefined {
  if (!beginnLocal || !hfMMDD) {
    return undefined;
  }
  const beginn = dayjs(beginnLocal, DATEFORMAT_LOCAL_STATE);
  const hf = dayjs(hfMMDD, HF_PARSE_FORMAT);
  if (!beginn.isValid() || !hf.isValid()) {
    return undefined;
  }
  return hf.year(beginn.year()).add(years, 'year').format(DATEFORMAT_LOCAL_STATE);
}

function hfPlusProduktLaufzeit(
  produkt: Produkt,
  beginnLocal: string,
  hauptfaelligkeitMMDD: string,
): string | undefined {
  if (produkt?.laufzeit === 1) {
    return hfPlusYears(beginnLocal, hauptfaelligkeitMMDD, 1);
  }
  if (produkt?.laufzeit === 3) {
    return hfPlusYears(beginnLocal, hauptfaelligkeitMMDD, 2);
  }
  return undefined;
}

function shortVorvertragWindowDays(produkt: Produkt): number | undefined {
  if (produkt?.laufzeit === 1) {
    return SHORT_VORVERTRAG_1Y_WINDOW_DAYS;
  }
  if (produkt?.laufzeit === 3) {
    return SHORT_VORVERTRAG_3Y_WINDOW_DAYS;
  }
  return undefined;
}

function ablaufForShortVorvertrag(
  produkt: Produkt,
  beginnLocal: string,
  vorvertragAblaufLocal: string | undefined,
  hauptfaelligkeitMMDD: string,
): string | undefined {
  const windowDays = shortVorvertragWindowDays(produkt);
  if (windowDays === undefined || !vorvertragAblaufLocal || !beginnLocal) {
    return hfPlusProduktLaufzeit(produkt, beginnLocal, hauptfaelligkeitMMDD);
  }
  const beginn = dayjs(beginnLocal, DATEFORMAT_LOCAL_STATE);
  const vorvertragAblauf = dayjs(vorvertragAblaufLocal, DATEFORMAT_LOCAL_STATE);
  if (!beginn.isValid() || !vorvertragAblauf.isValid()) {
    return hfPlusProduktLaufzeit(produkt, beginnLocal, hauptfaelligkeitMMDD);
  }
  const daysUntilAblauf = vorvertragAblauf.diff(beginn, 'day');
  if (daysUntilAblauf < windowDays) {
    const bumpYears = produkt.laufzeit === 1 ? 2 : 3;
    return hfPlusYears(beginnLocal, hauptfaelligkeitMMDD, bumpYears);
  }
  return hfPlusProduktLaufzeit(produkt, beginnLocal, hauptfaelligkeitMMDD);
}

function ablaufForLongVorvertrag(
  beginnLocal: string,
  vorvertragAblaufLocal: string | undefined,
  hauptfaelligkeitMMDD: string,
): string | undefined {
  if (!vorvertragAblaufLocal || !beginnLocal) {
    return undefined;
  }
  const beginn = dayjs(beginnLocal, DATEFORMAT_LOCAL_STATE);
  const vorvertragAblauf = dayjs(vorvertragAblaufLocal, DATEFORMAT_LOCAL_STATE);
  if (!beginn.isValid() || !vorvertragAblauf.isValid()) {
    return undefined;
  }
  const windowEnd = beginn.add(VORVERTRAG_ABLAUF_WINDOW_MONTHS, 'month');
  if (vorvertragAblauf.isAfter(windowEnd)) {
    return vorvertragAblauf.format(DATEFORMAT_LOCAL_STATE);
  }
  return hfPlusYears(beginnLocal, hauptfaelligkeitMMDD, MINDESTLAUFZEIT_YEARS);
}

function enforceMinimumMindestlaufzeit(ablaufLocal: string | undefined): string | undefined {
  if (!ablaufLocal) {
    return ablaufLocal;
  }
  const ablauf = dayjs(ablaufLocal, DATEFORMAT_LOCAL_STATE);
  if (!ablauf.isValid()) {
    return ablaufLocal;
  }
  const minimum = getCurrentDay().startOf('day').add(MINDESTLAUFZEIT_YEARS, 'year');
  let candidate = ablauf;
  while (candidate.isBefore(minimum)) {
    candidate = candidate.add(MINDESTLAUFZEIT_YEARS, 'year');
  }
  return candidate.format(DATEFORMAT_LOCAL_STATE);
}

export function calculateMindestlaufzeitAblauf(
  produkt: Produkt,
  beginnLocal: string,
  vorvertragLaufzeitYears: number | undefined,
  vorvertragAblaufLocal: string | undefined,
  hauptfaelligkeitMMDD: string,
): string | undefined {
  const isLongVorvertrag =
    vorvertragLaufzeitYears === undefined ||
    vorvertragLaufzeitYears > LONG_VORVERTRAG_LAUFZEIT_YEARS;

  const ablauf = isLongVorvertrag
    ? ablaufForLongVorvertrag(beginnLocal, vorvertragAblaufLocal, hauptfaelligkeitMMDD)
    : ablaufForShortVorvertrag(produkt, beginnLocal, vorvertragAblaufLocal, hauptfaelligkeitMMDD);

  return enforceMinimumMindestlaufzeit(ablauf);
}
